<script setup lang="ts">
  /**
   * SiteHeader — portal-style top bar: pill search with typeahead
   * suggestions. Like yahoo.com (US), the desktop header carries no
   * category links — categories live in the Explore More rail; on mobile
   * they collapse into the hamburger drawer, which is where the brand
   * wordmark lives.
   *
   * Lives in common (it is data-coupled) and is registered into lego-land's
   * component registry at boot (see plugins/legoland-registry.ts).
   *
   * The search pill is the platform's own Searchbox in its `top` variant --
   * the same component and variant production serves behind
   * searchBoxVariant=top -- so the portal gets the real typeahead,
   * monetized web-search navigation and search analytics rather than a
   * lookalike. The portal only supplies the band geometry it measured
   * against yahoo.com and its own surface colors.
   */
  import { computed, nextTick, ref, watch } from 'vue'
  import {
    onClickOutside,
    useEventListener,
    useScrollLock,
    useWindowScroll,
  } from '@vueuse/core'
  import Searchbox from '../Searchbox/Searchbox.vue'
  import { useEnhancedSearchbox } from '../../composables/data-fetching/useEnhancedSearchbox/useEnhancedSearchbox'

  export interface SiteHeaderProps {
    brandName?: string
    categories?: string[]
    searchPlaceholder?: string
  }

  const props = withDefaults(defineProps<SiteHeaderProps>(), {
    brandName: 'onenews',
    categories: () => ['News'],
    searchPlaceholder: 'Search the web',
  })

  const route = useRoute()

  // Like yahoo.com (US): the header band is the card surface color
  // (#201F1F dark) at all times; it starts tall with a roomy search pill
  // and compacts once the page is scrolled.
  const { y: scrollY } = useWindowScroll()
  const scrolled = computed(() => scrollY.value > 8)

  // --- Category nav (mobile hamburger drawer) ------------------------------

  const mobileMenuOpen = ref(false)
  const mobileMenuWrap = ref<HTMLElement | null>(null)
  const drawer = ref<HTMLElement | null>(null)
  const drawerClose = ref<HTMLElement | null>(null)
  const openerEl = ref<HTMLElement | null>(null)

  /**
   * The drawer is a modal surface, so it needs the focus contract to match:
   * focus moves in on open, Tab is kept inside while it is open, and focus
   * returns to the hamburger on close. Without this a keyboard user tabs
   * through the page behind the backdrop and never gets back.
   */
  const bodyScrollLocked = useScrollLock(
    import.meta.client ? document.body : null
  )

  watch(mobileMenuOpen, async (open) => {
    bodyScrollLocked.value = open
    if (open) {
      const active = document.activeElement
      openerEl.value = active instanceof HTMLElement ? active : null
      await nextTick()
      drawerClose.value?.focus()
      return
    }
    openerEl.value?.focus()
    openerEl.value = null
  })

  function keepFocusInDrawer(event: KeyboardEvent) {
    if (!mobileMenuOpen.value || event.key !== 'Tab' || !drawer.value) return

    const focusable = drawer.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled])'
    )
    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }
  onClickOutside(mobileMenuWrap, () => {
    mobileMenuOpen.value = false
  })

  const activeCategory = computed(() => {
    const fromQuery = route.query.category
    return typeof fromQuery === 'string' && props.categories.includes(fromQuery)
      ? fromQuery
      : props.categories[0]
  })

  const categoryLink = (category: string) => ({
    path: route.path,
    query: { ...route.query, category },
  })

  // Close the drawer after navigating
  watch(
    () => route.query.category,
    () => {
      mobileMenuOpen.value = false
    }
  )

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      mobileMenuOpen.value = false
    }
    keepFocusInDrawer(event)
  })

  // --- Search: platform typeahead + monetized navigation -------------------

  const {
    query,
    suggestions,
    handleEnterKey,
    handleSearchButton,
    handleSuggestionClick,
  } = useEnhancedSearchbox({
    enableSearchboxUrlNavigation: ref(true),
    suggestionType: 'simple',
    debounce: 90,
  })

  /**
   * At rest the search pill is left exactly as production serves it at
   * shiftnews.com/?searchBoxVariant=top, measured there: 748px wide at a
   * 1512px viewport (its own `lg:w-[50%]` capped to 990px), 56px tall, 100px
   * radius, a black 40x40 submit button with an 18px white glyph, on a
   * #404040 fill in dark and white in light. Nothing here restyles any of
   * that, so the two stay identical by construction.
   *
   * The only portal-specific behaviour is the collapse: once the header
   * sticks, the pill and its button shrink, the way yahoo.com's does. Scoped
   * to this wrapper so Searchbox itself, which Layouts/Home, ArticleSiderail,
   * pages/search and the onenews classic components all share, is untouched.
   */
  const SEARCHBOX_SHRINK_TRANSITION =
    '[&_.group]:transition-[height] [&_.group]:duration-200 [&_button.rounded-full]:transition-[height,width] [&_button.rounded-full]:duration-200'

  const searchboxCollapsed = computed(() =>
    scrolled.value
      ? '[&_.group]:!h-[44px] [&_button.rounded-full]:!h-9 [&_button.rounded-full]:!w-9'
      : ''
  )

  /**
   * Only the suggestion dropdown is repainted, so it sits on a portal surface
   * instead of the classic homepage's. The pill itself keeps its own tokens,
   * which is what makes it identical to production.
   */
  const searchboxTheme = {
    '--colors-background-default-strong': 'var(--yp-menu)',
    '--colors-action-neutral-subtle-hover': 'var(--yp-hover)',
  }
</script>

<template>
  <header class="sticky top-0 z-40 w-full bg-[var(--yp-header,#201f1f)]">
    <!-- Taller at the top of the page than when scrolled, like yahoo.com's
         search band, but trimmed from their 122px to 96px (SUP-686): every
         pixel above the fold pushes the right-rail ad units down, and AY
         only calls display() once enough of a lazy unit is in view, so a
         tighter band buys the second 300x250 real on-load viewability.
         Same 1374px container as the strip (measured: 32px side padding,
         16px gap). -->
    <!--
      At rest the row is full width with production's own 8px gutter, so the
      searchbox's `lg:w-[50%]` measures half the page exactly as it does on
      shiftnews.com. Constraining it to the 1374px band would silently shrink
      the pill (half of 1310 rather than half the page). Scrolled, the band
      does apply, which is the compact width measured on yahoo.com.
    -->
    <div
      class="mx-auto flex w-full items-center gap-3 transition-[height] duration-200 tablet:gap-4"
      :class="
        scrolled
          ? 'h-[64px] max-w-[1374px] px-4 tablet:justify-center tablet:px-8'
          : 'h-[64px] px-4 tablet:h-[96px] tablet:px-2'
      "
    >
      <!-- Mobile hamburger: the category nav collapses into a left drawer
           below desktop, like Yahoo's -->
      <div ref="mobileMenuWrap" class="desktop:hidden">
        <button
          type="button"
          aria-label="Open navigation menu"
          :aria-expanded="mobileMenuOpen"
          class="-ml-1 flex h-10 w-8 items-center justify-center text-content-primary"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <UIcon name="i-heroicons-bars-3" class="h-6 w-6" />
        </button>

        <!-- Backdrop -->
        <Transition
          enter-active-class="transition-opacity duration-200 ease-out"
          enter-from-class="opacity-0"
          leave-active-class="transition-opacity duration-150 ease-in"
          leave-to-class="opacity-0"
        >
          <div
            v-if="mobileMenuOpen"
            class="fixed inset-0 z-40 bg-black/50"
            aria-hidden="true"
            @click="mobileMenuOpen = false"
          />
        </Transition>

        <!-- Drawer -->
        <Transition
          enter-active-class="transition-transform duration-200 ease-out"
          enter-from-class="-translate-x-full"
          leave-active-class="transition-transform duration-150 ease-in"
          leave-to-class="-translate-x-full"
        >
          <nav
            v-if="mobileMenuOpen"
            ref="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            class="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col overflow-y-auto bg-[var(--yp-menu)] pb-6 shadow-2xl"
          >
            <!-- No wordmark in the drawer, matching the header itself -->
            <div class="flex items-center justify-end px-5 py-4">
              <button
                ref="drawerClose"
                type="button"
                aria-label="Close navigation menu"
                class="flex h-9 w-9 items-center justify-center text-content-primary/80 hover:text-content-primary"
                @click="mobileMenuOpen = false"
              >
                <UIcon name="i-heroicons-x-mark" class="h-6 w-6" />
              </button>
            </div>

            <NuxtLink
              v-for="category in categories"
              :key="category"
              :to="categoryLink(category)"
              class="px-5 py-3 text-[15px] transition-colors hover:bg-[var(--yp-hover)]"
              :class="
                category === activeCategory
                  ? 'font-semibold text-content-primary'
                  : 'font-normal text-content-primary/90'
              "
            >
              {{ category }}
            </NuxtLink>
          </nav>
        </Transition>
      </div>

      <!-- Search pill: the platform searchbox in its top variant. The pill
           sizes itself exactly as it does on shiftnews.com, so this wrapper
           only centres it and hands it the full band width to measure
           against. -->
      <div
        class="relative flex min-w-0 flex-1 justify-center"
        :class="[SEARCHBOX_SHRINK_TRANSITION, searchboxCollapsed]"
        :style="searchboxTheme"
      >
        <Searchbox
          v-model="query"
          top-search
          :sticky="false"
          :placeholder="searchPlaceholder"
          :suggestions="suggestions"
          @press-enter-on-search-input="handleEnterKey"
          @press-search-button="handleSearchButton"
          @click-suggestion-item="handleSuggestionClick"
        />
      </div>
    </div>
  </header>
</template>
